import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Building2,
  Sparkles,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Eye,
  Users,
  Award,
  ShieldCheck,
  HeartHandshake,
  Layers,
  Layout,
  Maximize,
  Compass,
  Hammer,
  Sparkle,
} from "lucide-react";

export const Route = createFileRoute("/services_/corporate-office-branding")({
  head: () => ({
    meta: [
      {
        title:
          "Corporate Office Branding Services — KMWAdvertising LLP",
      },
      {
        name: "description",
        content:
          "Transform Your Workplace into a Powerful Brand Experience with Corporate Office Branding by KMWAdvertising LLP. Wall murals, frosted glass, meeting room graphics & 3D environmental signs.",
      },
      {
        property: "og:title",
        content: "Corporate Office Branding Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Environmental graphics, wall murals, frosted glass branding, meeting room signs, and workspace graphics designed for corporate headquarters & office spaces.",
      },
    ],
  }),
  component: CorporateOfficeBrandingPage,
});

export function CorporateOfficeBrandingPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const whyMattersPillars = [
    {
      title: "Strengthen Brand Identity",
      desc: "Consistent branding throughout your office reinforces your company's visual identity and values.",
    },
    {
      title: "Create a Strong First Impression",
      desc: "Reception areas and client-facing spaces communicate professionalism and build trust from the moment visitors enter.",
    },
    {
      title: "Enhance Employee Engagement",
      desc: "A well-designed workspace promotes pride, motivation and a stronger sense of belonging among employees.",
    },
    {
      title: "Improve Customer Experience",
      desc: "Branded environments create memorable experiences that enhance customer confidence and loyalty.",
    },
    {
      title: "Reinforce Company Culture",
      desc: "Office branding reflects your mission, vision and organisational values, helping employees connect with your purpose every day.",
    },
  ];

  const wallGraphicsList = [
    "Inspirational Quote Walls",
    "Company Timeline Displays",
    "Brand Story Murals",
    "Mission & Vision Walls",
    "Core Values Graphics",
    "Achievement & Awards Displays",
    "Infographics",
    "Motivational Employee Graphics",
    "Custom Wall Murals",
    "Vinyl Wall Graphics",
  ];

  const meetingRoomList = [
    "Frosted Glass Branding",
    "Conference Room Graphics",
    "Custom Wall Art",
    "Branded Presentation Walls",
    "Digital Signage",
    "Room Naming Systems",
    "Glass Vinyl Graphics",
    "Acoustic Graphic Panels",
  ];

  const workspaceList = [
    "Department Branding",
    "Team Zone Graphics",
    "Employee Recognition Walls",
    "Workspace Graphics",
    "Desk Branding",
    "Collaboration Zone Graphics",
    "Innovation Walls",
    "Learning Spaces",
    "Wellness Area Branding",
    "Cafeteria Graphics",
  ];

  const glassBrandingList = [
    "Frosted Vinyl Graphics",
    "Branded Glass Films",
    "Privacy Patterns",
    "Corporate Logo Application",
    "Decorative Glass Designs",
    "Conference Room Branding",
    "Cabin Branding",
    "Safety Graphics",
  ];

  const environmentalGraphicsList = [
    "Large Format Graphics",
    "3D Installations",
    "Brand Murals",
    "Feature Walls",
    "Interactive Displays",
    "LED Branding Elements",
    "Illuminated Signage",
    "Digital Experience Zones",
    "Product Showcase Areas",
    "Innovation Centres",
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discovery & Consultation",
      desc: "We begin by understanding your brand identity, office layout, company culture and business goals.",
    },
    {
      step: "02",
      title: "Site Survey",
      desc: "Our team evaluates the available space, architecture, lighting and visitor flow to identify branding opportunities.",
    },
    {
      step: "03",
      title: "Concept Development",
      desc: "We develop creative concepts, mood boards and visual mock-ups that illustrate how branding elements will integrate within your workspace.",
    },
    {
      step: "04",
      title: "Design & Visualisation",
      desc: "Detailed artwork, 3D visualisations and material specifications are prepared for review and approval.",
    },
    {
      step: "05",
      title: "Production",
      desc: "Using premium materials and precision manufacturing techniques, we produce high-quality branding elements designed for durability and visual impact.",
    },
    {
      step: "06",
      title: "Installation",
      desc: "Our experienced installation team ensures seamless execution with minimal disruption to your daily business operations.",
    },
    {
      step: "07",
      title: "Quality Assurance",
      desc: "Every project undergoes a final inspection to ensure exceptional finish, consistency and adherence to brand standards.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Corporate Office Branding</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Building2 className="h-3.5 w-3.5" />
              Corporate Office & Environmental Branding
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Corporate Office Branding
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Transform Your Workplace into a Powerful Brand Experience
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                Your office is more than just a workplace. It is a physical representation of your brand, culture and values. Every visitor, client, employee and business partner who walks through your doors forms an impression based on the environment they experience. From reception areas and meeting rooms to workspaces and breakout zones, every element contributes to how your brand is perceived.
              </p>
              <p className="text-sm sm:text-base text-slate-400">
                A professionally branded office strengthens brand identity, enhances employee engagement and creates memorable experiences for customers and stakeholders. Whether you're welcoming potential clients, conducting investor meetings or onboarding new employees, your office should consistently reflect the professionalism and personality of your organisation.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                At KMWAdvertising LLP, we provide end-to-end Corporate Office Branding Services that combine strategic branding, interior graphics, environmental design and visual communication to transform workplaces into inspiring brand environments. With over 25 years of experience in branding, advertising and design, we create office spaces that reinforce your corporate identity while improving functionality and aesthetics.
              </p>
              <p className="text-xs sm:text-sm text-slate-400 italic font-semibold">
                From startups and corporate headquarters to manufacturing facilities, retail offices and co-working spaces, we deliver customised branding solutions that leave a lasting impression.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#office-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Request Office Site Survey</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#why-branding-matters"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Why Office Branding Matters</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Corporate Office Branding Matters */}
      <section id="why-branding-matters" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              Strategic Impact
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Why Corporate Office Branding Matters
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              A thoughtfully branded workplace goes beyond decoration. It communicates who you are, what you stand for and how you want your business to be remembered.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyMattersPillars.map((pillar) => (
              <div
                key={pillar.title}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition space-y-2"
              >
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-pink-400 shrink-0" />
                  {pillar.title}
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wall Graphics & Environmental Branding */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <Layout className="h-3.5 w-3.5" />
              Storytelling Walls
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Wall Graphics & Environmental Branding
            </h2>
            <p className="text-purple-300 font-semibold text-sm">Turn Empty Walls into Brand Storytelling Spaces</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Walls provide valuable opportunities to showcase your company's journey, achievements and culture. Our solutions include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {wallGraphicsList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-purple-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-purple-300/90 italic font-semibold">
            These installations transform ordinary office spaces into engaging brand experiences.
          </p>
        </div>
      </section>

      {/* Meeting Room & Conference Room Branding */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-mono font-bold uppercase">
              <Users className="h-3.5 w-3.5" />
              Collaborative Spaces
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Meeting Room & Conference Room Branding
            </h2>
            <p className="text-rose-300 font-semibold text-sm">Spaces That Inspire Collaboration</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Meeting rooms should reflect professionalism while supporting creativity and innovation. Our branding solutions include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {meetingRoomList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 text-center text-xs font-semibold text-white hover:border-rose-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-rose-300/90 italic font-semibold">
            These enhancements create a polished and inspiring environment for internal meetings and client presentations.
          </p>
        </div>
      </section>

      {/* Workspace & Employee Area Branding */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Building2 className="h-3.5 w-3.5" />
              Culture & Motivation
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Workspace & Employee Area Branding
            </h2>
            <p className="text-pink-300 font-semibold text-sm">Reinforce Company Culture Every Day</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Employee workspaces should motivate, inspire and reflect your organisation's values. Our workspace branding includes:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {workspaceList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-pink-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-pink-300/90 italic font-semibold">
            A visually engaging workplace contributes to higher morale, productivity and employee satisfaction.
          </p>
        </div>
      </section>

      {/* Glass Branding & Frosted Films */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <Maximize className="h-3.5 w-3.5" />
              Privacy & Elegance
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Glass Branding & Frosted Films
            </h2>
            <p className="text-purple-300 font-semibold text-sm">Modern Privacy with Professional Branding</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Glass partitions provide an excellent opportunity to combine privacy with elegant branding. We offer:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            {glassBrandingList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 text-center text-xs font-semibold text-white hover:border-purple-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-purple-300/90 italic font-semibold">
            These solutions enhance both aesthetics and functionality without compromising natural light.
          </p>
        </div>
      </section>

      {/* Corporate Environmental Graphics */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-mono font-bold uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              Immersive Spatial Design
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Corporate Environmental Graphics
            </h2>
            <p className="text-rose-300 font-semibold text-sm">Bring Your Brand to Life Throughout the Workplace</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Environmental graphics help create immersive spaces that communicate your organisation's personality. Our services include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {environmentalGraphicsList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-rose-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-rose-300/90 italic font-semibold">
            These installations help create memorable workplace experiences for employees and visitors alike.
          </p>
        </div>
      </section>

      {/* Our Office Branding Process */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Compass className="h-3.5 w-3.5" />
              7-Stage Execution Process
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Our Office Branding Process
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Every workplace is unique, which is why we follow a structured process to ensure every branding project aligns with your business objectives.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step) => (
              <div
                key={step.step}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 space-y-2 relative"
              >
                <span className="text-xs font-mono font-bold text-pink-400 bg-pink-500/10 px-2.5 py-1 rounded-full border border-pink-500/20">
                  Step {step.step}
                </span>
                <h3 className="text-base font-bold text-white pt-2">{step.title}</h3>
                <p className="text-xs text-slate-300 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="office-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Office Branding Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Transform Your Office Environment Today
              </h2>
              <p className="text-slate-300 text-sm">
                Book a site survey and workplace consultation with our environmental design specialists.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Site Survey Inquiry Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our environmental branding team will connect with you within 24 hours to coordinate your office survey.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Ramesh Varma"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Organization *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. InnoTech Global HQ"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="ramesh@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Branding Focus</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="complete">Complete Corporate Office Headquarters Overhaul</option>
                    <option value="reception">Reception Area & Client Meeting Room Branding</option>
                    <option value="wall-graphics">Wall Murals, Timeline & Environmental Graphics</option>
                    <option value="glass">Frosted Glass Films & Partition Branding</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Office Location & Sq. Ft. Estimate</label>
                  <textarea
                    rows={4}
                    placeholder="Provide details about office city/location, approximate square footage, or key target areas..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Schedule Site Survey & Consultation</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
